import { defineStore } from 'pinia';
import { z as zod } from 'zod';
import { useLS } from '@/scripts/utils';

/**
 * Zod schema for the SWA client principal returned by `/.auth/me`. Validates at the untrusted
 * browser → SWA control-plane boundary — a shape change upstream (or a gateway injecting a bad
 * payload) surfaces as an anonymous principal instead of an undefined deep in the user store.
 *
 * @see https://learn.microsoft.com/azure/static-web-apps/user-information
 */
const clientPrincipalSchema = zod.object({
  identityProvider: zod.string(),
  userId: zod.string(),
  // SWA CLI emulator omits userDetails when no username is set; prod AAD always supplies it.
  userDetails: zod.string().optional().default(''),
  userRoles: zod.array(zod.string()).optional(),
  claims: zod.array(zod.object({ typ: zod.string(), val: zod.string() })).optional(),
});

const authMeResponseSchema = zod.object({
  clientPrincipal: clientPrincipalSchema.nullable(),
});

/**
 * Azure Static Web Apps client principal shape, as returned by `/.auth/me`.
 */
export type ClientPrincipal = zod.infer<typeof clientPrincipalSchema>;

let principalCache: ClientPrincipal | null | undefined;

/**
 * Clears the in-module principal cache so the next `fetchPrincipal()` re-queries `/.auth/me` rather
 * than returning a stale authenticated value. Called by the API client when it detects that the SWA
 * session cookie has expired.
 */
export function invalidateSession(): void {
  principalCache = undefined;
}

/**
 * Fetches the current user's SWA client principal from `/.auth/me`, memoized for the page lifetime.
 * Returns `null` when the caller is anonymous or the endpoint errors. Pass `force=true` to bypass
 * the cache — the user-store `Reload` path does this on TTL expiry.
 *
 * @param force - When true, bypasses the in-module cache and re-queries `/.auth/me`.
 * @returns The authenticated principal, or `null` when anonymous or unreachable.
 */
export async function fetchPrincipal(force = false): Promise<ClientPrincipal | null> {
  if (!force && principalCache !== undefined) return principalCache;
  try {
    const res = await fetch('/.auth/me', { headers: { Accept: 'application/json' } });
    if (!res.ok) {
      principalCache = null;
      return null;
    }
    const parsed = authMeResponseSchema.safeParse(await res.json());
    if (!parsed.success) {
      console.error('/.auth/me response failed schema validation', parsed.error.issues);
      principalCache = null;
      return null;
    }
    principalCache = parsed.data.clientPrincipal;
    return principalCache;
  } catch {
    principalCache = null;
    return null;
  }
}

/**
 * Builds the SWA AAD sign-in URL with an explicit post-login redirect target. SWA's managed auth
 * round-trips to `/.auth/login/aad` and then 302s the browser back to `post_login_redirect_uri`.
 *
 * @param postLoginRedirectUri - Absolute or relative path to return to after successful auth.
 * @returns The `/.auth/login/aad?post_login_redirect_uri=…` URL ready for navigation.
 */
export function signInUrl(postLoginRedirectUri: string): string {
  const redirect = encodeURIComponent(postLoginRedirectUri || '/');
  return `/.auth/login/aad?post_login_redirect_uri=${redirect}`;
}

export function signOut(): void {
  window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
}

export const useUserStore = defineStore('user', {
  state: () => ({
    StoreExpireMinutes: 60,
    StoreExpire: useLS<Date>('user_StoreExpire', new Date()),
    AAD_User_FK: useLS<string>('user_AAD_User_FK', ''),
    Name: useLS<string>('user_Name', ''),
    Email: useLS<string>('user_Email', ''),
    Directory: useLS<string>('user_Directory', ''),
  }),
  actions: {
    async Reload(force = false) {
      if (!force && new Date() < new Date(this.StoreExpire)) return;
      const principal = await fetchPrincipal(true);
      if (!principal) {
        this.AAD_User_FK = '';
        this.Name = '';
        this.Email = '';
        return;
      }
      this.AAD_User_FK = principal.userId;
      this.Email = principal.userDetails;
      const nameClaim = principal.claims?.find(
        (c) =>
          c.typ === 'name' ||
          c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
      );
      this.Name = nameClaim?.val ?? principal.userDetails;
      this.StoreExpire = new Date(Date.now() + 60 * 1000 * this.StoreExpireMinutes);
    },
  },
});
